import { Component, OnInit } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import { filter, forkJoin, Observable, switchMap, take, tap } from 'rxjs';

import { FastenerBasePointHelper } from '../../../helpers/fastener-base-point.helper';
import { CuttingCustomHole, FastenerBasePointEnum } from '../../../models/cutting/cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from '../../../models/cutting/cutting-fixed-fasteners-set.model';
import { PlateTypeEnum } from '../../../models/cutting/cutting-panel-outline.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import { FacadeView } from '../../../models/cutting/facade-view.model';
import { LegacyDialogsService } from '../../../services/legacy-dialogs.service';
import {
  FastenersWizardDialogComponent,
  FastenersWizardDialogData,
} from '../../fasteners-wizard-dialog/fasteners-wizard-dialog.component';
import { FastenersWizardDialogResult } from '../../fasteners-wizard-dialog/fasteners-wizard-dialog.types';
import { FacadeSettingsDataService } from '../facade-settings-data.service';
import { FacadeSettingsStateService } from '../facade-settings-state.service';

export interface FastenerWithSide<T extends CuttingFixedFastenersSet | CuttingCustomHole> {
  side: Position;
  fastener: T;
}

export class FastenersBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _FastenersMixinBase = mixinDisabled(FastenersBase);

@Component({
  selector: 'csl-fasteners',
  styleUrls: ['./fasteners.component.scss'],
  templateUrl: './fasteners.component.html',
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
})
export class FastenersComponent extends _FastenersMixinBase implements OnInit, CanDisable {
  data$: Observable<any>;
  action$: Observable<any>;

  columns = ['name', 'base', 'coord', 'delete'];
  dataSource: FastenerWithSide<CuttingFixedFastenersSet | CuttingCustomHole>[] = [];

  constructor(
    private facadeSettingsService: FacadeSettingsStateService,
    private facadeDataService: FacadeSettingsDataService,
    private dialogs: LegacyDialogsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.data$ = this.facadeSettingsService.panel$.pipe(
      tap(
        (p: FacadeView) =>
          (this.dataSource = [
            ...this.setSide(p.panelOutline.topSide.fastenersSets, 'tc'),
            ...this.setSide(p.panelOutline.rightSide.fastenersSets, 'rc'),
            ...this.setSide(p.panelOutline.bottomSide.fastenersSets, 'bc'),
            ...this.setSide(p.panelOutline.leftSide.fastenersSets, 'lc'),
            ...this.setSide(p.panelOutline.topSide.customHoles, 'tc'),
            ...this.setSide(p.panelOutline.rightSide.customHoles, 'rc'),
            ...this.setSide(p.panelOutline.bottomSide.customHoles, 'bc'),
            ...this.setSide(p.panelOutline.leftSide.customHoles, 'lc'),
          ])
      )
    );
  }

  getBasePointAnglePosition(side: Position, basePoint: FastenerBasePointEnum): Position {
    return FastenerBasePointHelper.getBasePointAnglePosition(side, basePoint);
  }

  delete(item: CuttingFixedFastenersSet | CuttingCustomHole): void {
    this.action$ = this.facadeSettingsService.panel$.pipe(
      take(1),
      tap(p => {
        const remove = (list: (CuttingFixedFastenersSet | CuttingCustomHole)[]): void => {
          const index = list.indexOf(item);
          if (index >= 0) {
            list.splice(index, 1);
          }
        };

        remove(p.panelOutline.topSide.fastenersSets);
        remove(p.panelOutline.rightSide.fastenersSets);
        remove(p.panelOutline.bottomSide.fastenersSets);
        remove(p.panelOutline.leftSide.fastenersSets);
        remove(p.panelOutline.topSide.customHoles);
        remove(p.panelOutline.rightSide.customHoles);
        remove(p.panelOutline.bottomSide.customHoles);
        remove(p.panelOutline.leftSide.customHoles);

        this.facadeSettingsService.updatePanel(p);
      })
    );
  }

  addFastener(): void {
    let panel: FacadeView;
    this.action$ = forkJoin([
      this.facadeSettingsService.panel$.pipe(
        take(1),
        tap(p => (panel = p))
      ),
      this.facadeDataService.getAvailableHoleSchema(),
      this.facadeDataService.getAvailableFastenersSetSchema(),
    ]).pipe(
      switchMap(res =>
        this.dialogs.component<FastenersWizardDialogData, FastenersWizardDialogResult>(
          FastenersWizardDialogComponent,
          {
            panel: res[0],
            mainMaterialThickness: res[0].material ? res[0].material.thickness : 0,
            holeSchemas: res[1],
            holeSetShemas: res[2],
            availablePlate: PlateTypeEnum.Back,
            defaultMirrorAxis: 'x',
          },
          1000
        )
      ),
      filter(res => !!res),
      tap(res => this.setFastenersToPanel(panel, res))
    );
  }

  private setFastenersToPanel(panel: FacadeView, fasteners: FastenersWizardDialogResult): void {
    const adder = (top: any[], bot: any[], left: any[], right: any[], side: Position, val: any): void => {
      switch (side) {
        case 'tc':
          top.push(val);
          break;
        case 'bc':
          bot.push(val);
          break;
        case 'lc':
          left.push(val);
          break;
        case 'rc':
          right.push(val);
          break;
      }
    };

    (fasteners.holes || []).forEach(h => {
      adder(
        panel.panelOutline.topSide.customHoles,
        panel.panelOutline.bottomSide.customHoles,
        panel.panelOutline.leftSide.customHoles,
        panel.panelOutline.rightSide.customHoles,
        h.side,
        h.item
      );
    });

    (fasteners.sets || []).forEach(s => {
      adder(
        panel.panelOutline.topSide.fastenersSets,
        panel.panelOutline.bottomSide.fastenersSets,
        panel.panelOutline.leftSide.fastenersSets,
        panel.panelOutline.rightSide.fastenersSets,
        s.side,
        s.item
      );
    });

    this.facadeSettingsService.updatePanel(panel);
  }

  private setSide<T extends CuttingFixedFastenersSet | CuttingCustomHole>(
    list: T[],
    side: Position
  ): FastenerWithSide<CuttingFixedFastenersSet | CuttingCustomHole>[] {
    return list.map(f => ({ fastener: f, side: side }));
  }
}
